// Клиенти (04 §8, WP-C1/C5) — the businesses that said yes, and the money around them.

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { isConfigured, isDemoMode, supabase } from '../../lib/supabase.js';
import { MOCK_CHARGES, MOCK_CLIENTS } from '../../lib/mockData-ops.js';

export interface ClientRow {
  id: number;
  lead_id: number;
  package_key: string | null;
  one_time_price_eur: number | null;
  monthly_fee_eur: number | null;
  billing_day: number | null;
  go_live_date: string | null;
  status: 'active' | 'paused' | 'ended';
  notes: string | null;
  business_name: string;
}

export interface ChargeRow {
  id: number;
  client_id: number;
  kind: 'one_time' | 'fee' | 'expense';
  label: string;
  period_start: string | null;
  due_on: string;
  amount_eur: number;
  status: 'due' | 'paid' | 'waived';
  paid_at: string | null;
  supplier_paid_at: string | null;
}

export interface CashView {
  collected: number;
  outstanding: number;
  overdue: number;
  outOfPocket: number;
}

export interface ClientSite {
  id: number;
  url: string;
  monitor_uptime: boolean;
  monitor_ssl: boolean;
  domain_expiry: string | null;
  weekly_audit: boolean;
  audit_weekday: number | null;
}

export interface ClientTask {
  id: number;
  title: string;
  done: boolean;
  due: string | null;
}

const DEMO_TASKS: ClientTask[] = [
  { id: 1, title: 'Проверка на подновяване на SSL сертификат', done: true, due: '2026-09-01' },
  { id: 2, title: 'Добавяне на нов специалист в графика', done: false, due: '2026-09-20' },
];

export function useClients() {
  return useQuery({
    queryKey: ['clients'],
    queryFn: async (): Promise<ClientRow[]> => {
      if (!isConfigured || isDemoMode) {
        return MOCK_CLIENTS;
      }
      const { data, error } = await supabase
        .from('clients')
        .select(
          'id, lead_id, package_key, one_time_price_eur, monthly_fee_eur, billing_day,' +
            ' go_live_date, status, notes, leads(business_name)',
        )
        .order('status')
        .order('go_live_date', { ascending: false, nullsFirst: false });

      if (error) throw new Error(error.message);
      return (data ?? []).map((row) => {
        const record = row as unknown as Record<string, unknown>;
        const lead = record['leads'] as { business_name?: string } | null;
        return {
          ...(record as unknown as Omit<ClientRow, 'business_name'>),
          business_name: lead?.business_name ?? `#${String(record['lead_id'])}`,
        };
      });
    },
  });
}

export function useClient(clientId: number | null) {
  return useQuery({
    queryKey: ['client', clientId],
    enabled: clientId !== null,
    queryFn: async (): Promise<ClientRow | null> => {
      if (!isConfigured || isDemoMode) {
        return MOCK_CLIENTS.find((c) => c.id === clientId) ?? MOCK_CLIENTS[0] ?? null;
      }
      const { data, error } = await supabase
        .from('clients')
        .select(
          'id, lead_id, package_key, one_time_price_eur, monthly_fee_eur, billing_day,' +
            ' go_live_date, status, notes, leads(business_name)',
        )
        .eq('id', clientId!)
        .maybeSingle();

      if (error) throw new Error(error.message);
      if (!data) return null;
      const record = data as unknown as Record<string, unknown>;
      const lead = record['leads'] as { business_name?: string } | null;
      return {
        ...(record as unknown as Omit<ClientRow, 'business_name'>),
        business_name: lead?.business_name ?? `#${String(record['lead_id'])}`,
      };
    },
  });
}

export function useCharges(clientId: number | null) {
  return useQuery({
    queryKey: ['client_charges', clientId],
    enabled: clientId !== null,
    queryFn: async (): Promise<ChargeRow[]> => {
      if (!isConfigured || isDemoMode) {
        return MOCK_CHARGES.filter((c) => c.client_id === clientId);
      }
      const { data, error } = await supabase
        .from('client_charges')
        .select(
          'id, client_id, kind, label, period_start, due_on, amount_eur, status, paid_at, supplier_paid_at',
        )
        .eq('client_id', clientId!)
        .order('due_on', { ascending: false });
      if (error) throw new Error(error.message);
      return (data ?? []) as unknown as ChargeRow[];
    },
  });
}

export function useAllCharges() {
  return useQuery({
    queryKey: ['client_charges', 'all'],
    queryFn: async (): Promise<ChargeRow[]> => {
      if (!isConfigured || isDemoMode) {
        return MOCK_CHARGES;
      }
      const { data, error } = await supabase
        .from('client_charges')
        .select(
          'id, client_id, kind, label, period_start, due_on, amount_eur, status, paid_at, supplier_paid_at',
        )
        .limit(5000);
      if (error) throw new Error(error.message);
      return (data ?? []) as unknown as ChargeRow[];
    },
  });
}

export function cashView(charges: readonly ChargeRow[], today = new Date()): CashView {
  const day = today.toISOString().slice(0, 10);
  let collected = 0;
  let outstanding = 0;
  let overdue = 0;
  let outOfPocket = 0;

  for (const charge of charges) {
    const amount = Number(charge.amount_eur);
    if (charge.status === 'paid') collected += amount;
    if (charge.status === 'due') {
      outstanding += amount;
      if (charge.due_on < day) overdue += amount;
    }
    if (charge.kind === 'expense' && charge.supplier_paid_at !== null && charge.status !== 'paid') {
      outOfPocket += amount;
    }
  }

  return { collected, outstanding, overdue, outOfPocket };
}

export function useMarkPaid() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, paid }: { id: number; paid: boolean }) => {
      if (!isConfigured || isDemoMode) {
        const row = MOCK_CHARGES.find((c) => c.id === id);
        if (row) row.status = paid ? 'paid' : 'due';
        return;
      }
      const { error } = await supabase
        .from('client_charges')
        .update(
          paid
            ? { status: 'paid', paid_at: new Date().toISOString().slice(0, 10) }
            : { status: 'due', paid_at: null },
        )
        .eq('id', id);
      if (error) throw new Error(error.message);
    },
    onSuccess: () => void client.invalidateQueries({ queryKey: ['client_charges'] }),
  });
}

export function useClientSites(clientId: number | null) {
  return useQuery({
    queryKey: ['client_sites', clientId],
    enabled: clientId !== null,
    queryFn: async (): Promise<ClientSite[]> => {
      if (!isConfigured || isDemoMode) {
        return [
          {
            id: 1,
            url: 'https://dentallazur.com',
            monitor_uptime: true,
            monitor_ssl: true,
            domain_expiry: '2027-06-20',
            weekly_audit: true,
            audit_weekday: 1,
          },
        ];
      }
      const { data, error } = await supabase
        .from('client_sites')
        .select('id, url, monitor_uptime, monitor_ssl, domain_expiry, weekly_audit, audit_weekday')
        .eq('client_id', clientId!);
      if (error) throw new Error(error.message);
      return (data ?? []) as unknown as ClientSite[];
    },
  });
}

export function useClientTasks(clientId: number | null) {
  return useQuery({
    queryKey: ['client_tasks', clientId],
    enabled: clientId !== null,
    queryFn: async (): Promise<ClientTask[]> => {
      if (!isConfigured || isDemoMode) {
        return DEMO_TASKS;
      }
      const { data, error } = await supabase
        .from('client_tasks')
        .select('id, title, done, due')
        .eq('client_id', clientId!)
        .order('done')
        .order('due', { nullsFirst: false });
      if (error) throw new Error(error.message);
      return (data ?? []) as unknown as ClientTask[];
    },
  });
}

export function useToggleTask() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, done }: { id: number; done: boolean }) => {
      if (!isConfigured || isDemoMode) {
        const task = DEMO_TASKS.find((t) => t.id === id);
        if (task) task.done = done;
        return;
      }
      const { error } = await supabase.from('client_tasks').update({ done }).eq('id', id);
      if (error) throw new Error(error.message);
    },
    onSuccess: () => void client.invalidateQueries({ queryKey: ['client_tasks'] }),
  });
}

export function useAddTask(clientId: number | null) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async ({ title, due }: { title: string; due: string | null }) => {
      if (!isConfigured || isDemoMode) {
        DEMO_TASKS.push({ id: Date.now(), title, done: false, due });
        return;
      }
      const { error } = await supabase
        .from('client_tasks')
        .insert({ client_id: clientId, title, due });
      if (error) throw new Error(error.message);
    },
    onSuccess: () => void client.invalidateQueries({ queryKey: ['client_tasks', clientId] }),
  });
}

export function useConvertLead() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      leadId: number;
      packageKey: string;
      oneTimePriceEur: number | null;
      monthlyFeeEur: number | null;
      billingDay: number;
      goLiveDate: string | null;
      siteUrl: string | null;
    }) => {
      if (!isConfigured || isDemoMode) {
        return 999;
      }
      const { data, error } = await supabase
        .from('clients')
        .insert({
          lead_id: input.leadId,
          package_key: input.packageKey,
          one_time_price_eur: input.oneTimePriceEur,
          monthly_fee_eur: input.monthlyFeeEur,
          billing_day: input.billingDay,
          go_live_date: input.goLiveDate,
          status: 'active',
        })
        .select('id')
        .single();
      if (error) throw new Error(error.message);
      const clientId = data.id as number;

      // The dialog collects the site and promises the one-time price "се начислява веднага";
      // neither was written before, so monitoring never started and the charge never existed.
      if (input.siteUrl !== null && input.siteUrl.trim() !== '') {
        const { error: siteError } = await supabase
          .from('client_sites')
          .insert({ client_id: clientId, url: input.siteUrl.trim() });
        if (siteError) throw new Error(siteError.message);
      }
      if (input.oneTimePriceEur !== null && input.oneTimePriceEur > 0) {
        const { error: chargeError } = await supabase.from('client_charges').insert({
          client_id: clientId,
          kind: 'one_time',
          label: `Еднократно · ${input.packageKey}`,
          due_on: new Date().toISOString().slice(0, 10),
          amount_eur: input.oneTimePriceEur,
          status: 'due',
        });
        if (chargeError) throw new Error(chargeError.message);
      }
      return clientId;
    },
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ['clients'] });
      void client.invalidateQueries({ queryKey: ['leads'] });
      void client.invalidateQueries({ queryKey: ['won_leads'] });
      void client.invalidateQueries({ queryKey: ['client_sites'] });
      void client.invalidateQueries({ queryKey: ['client_charges'] });
    },
  });
}
